import * as React from "react";
import { cx } from "../util.js";

export type BadgeVariant = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Color intent. @default "neutral" */
  variant?: BadgeVariant;
  /** Show a leading status dot in the current color. */
  dot?: boolean;
}
/** Compact pill for status/labels. Pairs a tinted bg with solid-color text. */
export function Badge({ variant = "neutral", dot, className, children, ...rest }: BadgeProps) {
  return (
    <span className={cx("ip-badge", className)} data-variant={variant} {...rest}>
      {dot && <span className="ip-dot" style={{ background: "currentColor" }} aria-hidden />}
      {children}
    </span>
  );
}

export type SceneStatus = "draft" | "revised" | "final" | "idle";
const STATUS_VARIANT: Record<SceneStatus, BadgeVariant> = {
  draft: "warning",
  revised: "info",
  final: "success",
  idle: "neutral",
};
const STATUS_LABEL: Record<SceneStatus, string> = {
  draft: "Draft",
  revised: "Revised",
  final: "Final",
  idle: "Idle",
};

export interface StatusBadgeProps extends Omit<BadgeProps, "variant" | "dot"> {
  status: SceneStatus;
  /** Override the default label text. */
  label?: string;
}
/** Scene status badge mapped to the canonical status colors. */
export function StatusBadge({ status, label, ...rest }: StatusBadgeProps) {
  return (
    <Badge variant={STATUS_VARIANT[status]} dot {...rest}>
      {label ?? STATUS_LABEL[status]}
    </Badge>
  );
}
